import { useCallback } from 'react';
import { type AuthError } from '@atlaskit/outbound-auth-flow-client';
import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import {
	type GenericShowFlag,
	Integration,
	type ScopeState,
	type SlackWorkspace,
} from '../../../types';
import { useThirdPartyIntegrationsFlags } from '../../Flags/ThirdPartyIntegrations';
import { PLATFORM_SLACK_SERVICE } from '../constants';
import { beginThirdPartyOauth } from '../utils';
import { type ThirdPartyContextAnalytics } from '.';

export interface Arguments {
	analytics: ThirdPartyContextAnalytics;
	integrations: Integration[] | null;
	onThirdPartyIntegrationsChange: () => Promise<void>;
	openSlackSettings: () => void;
	setIsAfterSlackConnect: (value: boolean) => void;
	showFlag?: GenericShowFlag<any>;
	slackWorkspaces: SlackWorkspace[];
}

export const useOnConnectProvider = ({
	analytics,
	integrations,
	onThirdPartyIntegrationsChange,
	openSlackSettings,
	setIsAfterSlackConnect,
	showFlag,
	slackWorkspaces,
}: Arguments) => {
	const thirdPartyIntegrationsFlags = useThirdPartyIntegrationsFlags();
	return useCallback(
		(args: {
			serviceKey: Integration;
			forceConnect?: boolean;
			fromUpdateConnection?: boolean;
			scopeState?: ScopeState;
			customAnalyticsFn?: (
				createAnalyticsEvent: CreateUIAnalyticsEvent,
				serviceKey: string,
			) => void;
		}) => {
			const { serviceKey, scopeState } = args;

			// Open slack settings instead of reconnecting if we are already
			// connected and have slack workspaces with valid scope
			if (
				serviceKey === PLATFORM_SLACK_SERVICE &&
				integrations?.includes(PLATFORM_SLACK_SERVICE) &&
				slackWorkspaces.length > 0
			) {
				openSlackSettings();
				return;
			}

			beginThirdPartyOauth(serviceKey, (success: boolean, error?: AuthError) => {
				onThirdPartyIntegrationsChange().then(() => {
					// show slack workspaces dialog if just connected platform slack
					if (success && serviceKey === Integration.PLATFORM_SLACK_SERVICE) {
						setIsAfterSlackConnect(true);
					}
				});

				if (success) {
					analytics.integrationConnected({
						serviceKey: serviceKey,
						previousStatus: scopeState,
					});

					if (serviceKey !== PLATFORM_SLACK_SERVICE && showFlag) {
						showFlag(thirdPartyIntegrationsFlags.success(serviceKey));
					}
				} else {
					analytics.integrationFailed(serviceKey, error);
				}
			});
		},
		[
			analytics,
			integrations,
			onThirdPartyIntegrationsChange,
			openSlackSettings,
			setIsAfterSlackConnect,
			showFlag,
			slackWorkspaces.length,
			thirdPartyIntegrationsFlags,
		],
	);
};
